import { Box, CircularProgress } from '@mui/material';
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Routes } from '../App';

type ConfigParamValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>;

export interface Config {
  url: string;
  params: Record<string, ConfigParamValue>;
}

const DEFAULT_CONFIG: Config = {
  url: '',
  params: {},
};

const ConfigContext = createContext<Config>(DEFAULT_CONFIG);
export const useConfig = () => useContext(ConfigContext);

interface ConfigRedirectProviderProps {
  children: ReactNode;
}

const ROUTE_SEGMENTS = new Set(
  Object.values(Routes)
    .filter(route => route.startsWith('/'))
    .map(route => route.split('/').filter(Boolean)[0]),
);

export const ConfigRedirectProvider = ({
  children,
}: ConfigRedirectProviderProps) => {
  const [config, setConfig] = useState<Config>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const { pathname, search } = window.location;

      let configId = sessionStorage.getItem('mytraits_config_id');
      const alreadyRedirected = sessionStorage.getItem(
        'mytraits_config_redirected',
      );

      let configFromQuery = false;

      if (!configId) {
        const params = new URLSearchParams(search);
        const queryConfig = params.get('config');

        if (queryConfig) {
          configId = queryConfig;
          configFromQuery = true;
          sessionStorage.setItem('mytraits_config_redirected', 'true');
        } else {
          const segments = pathname.split('/').filter(Boolean);

          if (segments.length === 1) {
            const candidate = segments[0];

            if (!ROUTE_SEGMENTS.has(candidate)) {
              configId = candidate;
            }
          }
        }

        if (configId) {
          sessionStorage.setItem('mytraits_config_id', configId);
        }
      }

      if (!configId) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(
          `https://config.mytraits.ai/fetch-config/${configId}`,
        );
        if (!res.ok) throw new Error('Config fetch failed');

        const data: Config = await res.json();
        setConfig(data);

        if (configFromQuery) {
          setLoading(false);
          return;
        }

        if (data?.url && !alreadyRedirected) {
          const current = window.location.href;
          const target = new URL(data.url, window.location.origin).href;

          if (current !== target) {
            sessionStorage.setItem('mytraits_config_redirected', 'true');
            window.location.replace(target);
            return;
          }
        }
      } catch (e) {
        console.error('Config fetch error', e);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#fff',
          zIndex: 10000,
        }}
      >
        <CircularProgress size={32} style={{ color: '#040B1D' }} />
      </Box>
    );
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
};
